/**
 * URL Security Validation
 *
 * Validates that environment variables containing URLs use HTTPS in production.
 * This prevents mixed content warnings and security issues.
 *
 * Usage:
 *   import { validateProductionUrls } from '@/lib/env/validateUrls';
 *   validateProductionUrls(); // Call at app startup
 */

const env = import.meta.env;
const APP_ENV = env.VITE_APP_ENV || 'development';
const IS_PROD_OR_STAGING = APP_ENV === 'production' || APP_ENV === 'staging';

/**
 * Environment variables that should contain HTTPS URLs in production
 * Format: [envVarName, description, isRequired]
 */
const URL_ENV_VARS: [string, string, boolean][] = [
  ['VITE_RDW_LOOKUP_URL', 'RDW Lookup API', false],
  ['VITE_FUNCTIONS_BASE_URL', 'Cloud Functions Base URL', false],
  ['VITE_TWINFIELD_AUTH_START_URL', 'Twinfield OAuth URL', false],
  ['VITE_EXACT_AUTH_START_URL', 'Exact OAuth URL', false],
  ['VITE_POSTHOG_HOST', 'PostHog Analytics Host', false],
  ['VITE_SENTRY_DSN', 'Sentry DSN', false],
];

/**
 * URLs that are allowed to use HTTP (local development only)
 */
const ALLOWED_HTTP_PATTERNS = [
  /^http:\/\/localhost/i,
  /^http:\/\/127\.0\.0\.1/i,
  /^http:\/\/0\.0\.0\.0/i,
];

/**
 * Check if a URL is allowed to use HTTP
 */
function isAllowedHttpUrl(url: string): boolean {
  return ALLOWED_HTTP_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Validate a single URL environment variable
 * Returns error message if invalid, null if valid
 */
function validateUrlVar(name: string, description: string): string | null {
  const value = env[name];

  // Skip if not set (optional vars)
  if (!value) {
    return null;
  }

  // Check if it's an HTTP URL
  if (value.startsWith('http://')) {
    // Allow localhost in development
    if (!IS_PROD_OR_STAGING && isAllowedHttpUrl(value)) {
      return null;
    }

    // In production, HTTP is not allowed
    if (IS_PROD_OR_STAGING) {
      return `${name} (${description}): Uses insecure http:// - must use https:// in production`;
    }

    // In development, warn but don't fail for non-localhost HTTP
    if (!isAllowedHttpUrl(value)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Security] ${name} uses http:// which is insecure. ` +
          `Consider using https:// or localhost for development.`
      );
    }
  }

  return null;
}

/**
 * Validate all URL environment variables
 *
 * In production/staging: Throws error if any URL uses HTTP
 * In development: Logs warnings for insecure URLs
 *
 * @throws Error if insecure URLs detected in production/staging
 */
export function validateProductionUrls(): void {
  const errors: string[] = [];

  for (const [name, description] of URL_ENV_VARS) {
    const error = validateUrlVar(name, description);
    if (error) {
      errors.push(error);
    }
  }

  if (errors.length > 0 && IS_PROD_OR_STAGING) {
    const errorMsg =
      `[Security] FATAL: Insecure HTTP URLs detected in ${APP_ENV} environment:\n\n` +
      errors.map((e) => `  ❌ ${e}`).join('\n') +
      `\n\nAll URLs must use HTTPS in production. ` +
      `Update your environment variables and restart.`;

    // eslint-disable-next-line no-console
    console.error(errorMsg);
    throw new Error(errorMsg);
  }
}

/**
 * Check if a URL is secure (HTTPS or allowed HTTP)
 * Use this for runtime URL validation
 */
export function isSecureUrl(url: string): boolean {
  if (url.startsWith('https://')) {
    return true;
  }

  if (url.startsWith('http://')) {
    // Allow localhost in any environment
    return isAllowedHttpUrl(url);
  }

  // Relative URLs are considered secure
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
    return true;
  }

  // Protocol-relative URLs inherit the page's protocol
  if (url.startsWith('//')) {
    return true;
  }

  return false;
}

/**
 * Assert that a URL is secure, throw if not
 * Use this for critical URLs that must be HTTPS
 */
export function assertSecureUrl(url: string, context: string): void {
  if (!isSecureUrl(url)) {
    throw new Error(
      `[Security] Insecure URL detected in ${context}: ${url}\n` +
        `URLs must use HTTPS or be localhost for development.`
    );
  }
}

/**
 * Get a validated URL from environment, ensuring it's secure
 * Returns undefined if not set, throws if insecure in production
 */
export function getSecureEnvUrl(envVar: string): string | undefined {
  const value = env[envVar];

  if (!value) {
    return undefined;
  }

  if (IS_PROD_OR_STAGING && !isSecureUrl(value)) {
    throw new Error(
      `[Security] Environment variable ${envVar} contains insecure URL: ${value}\n` +
        `All URLs must use HTTPS in production.`
    );
  }

  return value;
}
